import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
} from "@mui/material";
import { PhotoCamera, PictureAsPdf, VideoLibrary } from "@mui/icons-material";
import {
  Image as ImageIcon,
  MessageSquare,
  Phone,
  Link as LinkIcon,
  X,
  Plus,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

export default function WhatsAppMessagePreview({ id, isViewMode }) {
  const [templateType, setTemplateType] = useState("");
  const [templateFormat, setTemplateFormat] = useState("");
  const [templateFooter, setTemplateFooter] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [interactiveAction, setInteractiveAction] = useState("none");
  const [quickReplies, setQuickReplies] = useState([]);
  const [newQuickReply, setNewQuickReply] = useState("");
  const [whatsappBalance, setWhatsappBalance] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null); // For image preview
  const [fileType, setFileType] = useState("");
  const [callToActions, setCallToActions] = useState([
    {
      type: "",
      buttonTitle: "",
      buttonValue: "",
      countryCode: "+91", // New state for country code
    },
  ]);

  const downloadFile = (fileUrl) => {
    if (!fileUrl) {
      toast.error("No file available to download.");
      return;
    }

    const fileName = fileUrl.split("/").pop();
    console.log(fileUrl);

    saveAs(BASE_URL + fileUrl, fileName);
  };

  const getFileType = (fileUrl) => {
    if (!fileUrl) return null;

    const extension = fileUrl.split(".").pop().toLowerCase();

    if (["png", "jpg", "jpeg"].includes(extension)) return "image";
    if (["mp4", "mov", "avi"].includes(extension)) return "video";
    if (["pdf"].includes(extension)) return "pdf";

    return "unknown"; // Default case if format is unsupported
  };

  const getUserTemplateById = () => {
    axios
      .get(`${BASE_URL}admin/getUserTemplate/` + id, {
        headers: {
          "Content-Type": "Application/json",
          version: "1.0.0",
          "x-access-token": `${token}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          setTemplateData(response.data.data);
        } else {
          toast.error("Unable to load template");
        }
      })
      .catch((error) => {
        toast.error("Unable to load templates..please contact admin");
      });
  };

  // Set template data with proper phone number parsing
  const setTemplateData = (template) => {
    setTemplateName(template.templateName || "");
    setTemplateType(template.templateType || "");
    setTemplateFormat(template.templateFormat || "");
    setTemplateFooter(template.templateFooter || "");
    setImageFile(template.fileUrl || "");
    setPreviewUrl(template.fileUrl ? `${BASE_URL}${template.fileUrl}` : "");
    setFileType(getFileType(template.fileUrl));

    // Determine interactive action type
    setInteractiveAction(
      template.callToActions?.length > 0 && template.quickReplies?.length > 0
        ? "all"
        : template.callToActions?.length > 0
        ? "cta"
        : template.quickReplies?.length > 0
        ? "quick"
        : "none"
    );

    setQuickReplies(template.quickReplies || []);

    // Process callToActions to split phone numbers
    const processedCTAs =
      template.callToActions?.length > 0
        ? template.callToActions.map((cta) => {
            if (cta.type === "phone" && cta.buttonValue) {
              const countryCode = cta.countryCode || "+91"; // Get from backend or default to +91
              const escapedCountryCode = countryCode.replace(/\+/g, "\\+"); // Escape `+`
              const regex = new RegExp(`^(${escapedCountryCode})(\\d+)$`); // Match dynamic country code
              const match = cta.buttonValue.match(regex);

              return {
                ...cta,
                buttonValue: match ? match[2] : cta.buttonValue, // Remove country code
                countryCode, // Show country code from backend
              };
            }
            return cta;
          })
        : [{ type: "", buttonTitle: "", buttonValue: "", countryCode: "+91" }];

    setCallToActions(processedCTAs);

    setCallToActions(processedCTAs);
  };

  useEffect(() => {
    const theme = localStorage.getItem("theme");
    document.body.className = theme;
    if (isViewMode && id) {
      getUserTemplateById();
    }
    // Fetch user balance
    fetchUserBalance();
  }, [id, isViewMode]);

  const fetchUserBalance = () => {
    axios
      .get(`${BASE_URL}auth/view-profile`, {
        headers: {
          "Content-Type": "Application/json",
          version: "1.0.0",
          "x-access-token": `${token}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          const balance = response.data.data.whatsapp_balance || 0;
          setWhatsappBalance(balance);
          if (balance === 0) {
            setShowWarning(true);
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Character limits
  const maxChars = 1024;
  const footerMaxChars = 60;
  const buttonTitleMaxChars = 25;
  const quickReplyMaxChars = 25;
  const maxQuickReplies = 5;

  // Handle CTA changes
  const handleCallToActionChange = (index, field, value) => {
    const updatedActions = [...callToActions];
    updatedActions[index] = { ...updatedActions[index], [field]: value };
    setCallToActions(updatedActions);
  };

  // Add/remove quick replies
  const handleQuickReplyAdd = () => {
    if (newQuickReply && quickReplies.length < maxQuickReplies) {
      setQuickReplies([...quickReplies, newQuickReply]);
      setNewQuickReply("");
    }
  };

  const removeQuickReply = (index) => {
    setQuickReplies(quickReplies.filter((_, i) => i !== index));
  };

  // Get responsive phone dimensions based on screen size
  const getPhoneDimensions = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // Calculate max available dimensions (optimized to reduce whitespace)
    const maxAvailableHeight = screenHeight - 200; // Reduced from 250 to 200
    
    // Calculate available width based on screen size
    // Account for sidebar, padding, and the form column taking roughly 50% on desktop
    let maxAvailableWidth;
    if (screenWidth < 768) {
      // Mobile: full width minus padding
      maxAvailableWidth = screenWidth - 40;
    } else if (screenWidth < 1024) {
      // Tablet: consider 2-column layout
      maxAvailableWidth = (screenWidth * 0.45) - 40;
    } else {
      // Desktop: consider sidebar + 2-column layout
      maxAvailableWidth = (screenWidth - 250) * 0.45 - 40; // 250px for sidebar
    }
    
    // Define ideal phone dimensions
    const idealWidth = 320;
    const idealHeight = 600;
    const aspectRatio = idealHeight / idealWidth; // ~1.875
    
    // Calculate dimensions that fit within available space while maintaining aspect ratio
    let phoneWidth = Math.min(idealWidth, maxAvailableWidth);
    let phoneHeight = phoneWidth * aspectRatio;
    
    // If height exceeds available space, scale down by height instead
    if (phoneHeight > maxAvailableHeight) {
      phoneHeight = maxAvailableHeight;
      phoneWidth = phoneHeight / aspectRatio;
    }
    
    // Enforce minimum size for usability
    const minWidth = 240;
    if (phoneWidth < minWidth) {
      phoneWidth = minWidth;
      phoneHeight = phoneWidth * aspectRatio;
    }
    
    const scale = phoneWidth / idealWidth;
    
    return {
      width: phoneWidth,
      height: phoneHeight,
      borderWidth: 8 * scale,
      borderRadius: 45 * scale,
      scale: scale,
    };
  };

  const [dimensions, setDimensions] = useState(getPhoneDimensions());

  useEffect(() => {
    const handleResize = () => {
      setDimensions(getPhoneDimensions());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scale = dimensions.scale;

  // Phone preview styles
  const phoneStyles = {
    container: {
      position: "relative",
      margin: "0 auto",
      border: `${dimensions.borderWidth}px solid #1A1A1A`,
      borderRadius: `${dimensions.borderRadius}px`,
      height: `${dimensions.height}px`,
      width: `${dimensions.width}px`,
      background: "#000",
      boxShadow:
        "0 0 20px rgba(0, 0, 0, 0.2), inset 0 0 10px rgba(255, 255, 255, 0.1)",
      overflow: "hidden",
    },

    dynamicIsland: {
      position: "absolute",
      top: `${12 * scale}px`,
      left: "50%",
      transform: "translateX(-50%)",
      width: `${120 * scale}px`,
      height: `${25 * scale}px`,
      background: "#000",
      borderRadius: `${20 * scale}px`,
      zIndex: 10,
    },
    screen: {
      height: "100%",
      background: "#fff",
      position: "relative",
      overflow: "hidden",
      borderRadius: `${40 * scale}px`,
      display: "flex",
      flexDirection: "column",
    },
    statusBar: {
      position: "absolute",
      top: `${5 * scale}px`,
      left: 0,
      right: 0,
      padding: `${15 * scale}px`,
      display: "flex",
      justifyContent: "space-between",
      color: "#000",
      fontSize: `${14 * scale}px`,
      zIndex: 5,
    },
    whatsappHeader: {
      background: "#075E54",
      color: "#fff",
      padding: `${60 * scale}px ${16 * scale}px ${16 * scale}px`,
      display: "flex",
      alignItems: "center",
      gap: `${12 * scale}px`,
      flexShrink: 0,
    },
    chatBackground: {
      background: "#E5DDD5",
      flex: 1,
      padding: `${16 * scale}px`,
      overflowY: "auto",
      overflowX: "hidden",
    },
    messageBubble: {
      maxWidth: "85%",
      marginLeft: "auto",
      background: "#fff",
      borderRadius: `${12 * scale}px`,
      padding: `${12 * scale}px`,
      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
      marginBottom: `${4 * scale}px`,
      fontSize: `${14 * scale}px`,
    },
    imagePreview: {
      width: "100%",
      aspectRatio: "16/9",
      background: "#f0f0f0",
      borderRadius: `${8 * scale}px`,
      marginBottom: `${8 * scale}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    actionButton: {
      width: "100%",
      background: "#fff",
      border: "1px solid #128C7E",
      borderRadius: `${8 * scale}px`,
      color: "#128C7E",
      fontSize: `${14 * scale}px`,
      paddingTop: `${4 * scale}px`,
      paddingBottom: `${4 * scale}px`,
      textAlign: "center",
      cursor: "pointer",
      margin: `${1 * scale}px`,
    },
    actionButtonWrapper: {
      width: "85%",
      marginLeft: "auto",
      marginTop: `${4 * scale}px`,
      cursor: "pointer",
    },
    quickReply: {
      width: "100%",
      background: "#d9d4d9",
      border: "1px solid #128C7E",
      borderRadius: `${8 * scale}px`,
      color: "#128C7E",
      fontSize: `${14 * scale}px`,
      paddingTop: `${4 * scale}px`,
      paddingBottom: `${4 * scale}px`,
      textAlign: "center",
      cursor: "pointer",
      margin: `${1 * scale}px`,
    },
  };

  const handleInteractiveChange = (event) => {
    const value = event.target.value;
    setInteractiveAction(value);
    if (value === "none") {
      setCallToActions([]);
      setQuickReplies([]);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Check if user has 0 credits
    if (whatsappBalance === 0) {
      toast.error("Insufficient WhatsApp credits. Please recharge your account to create WhatsApp templates.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("templateName", templateName);
      formData.append("templateType", templateType);
      formData.append("templateFormat", templateFormat);
      formData.append("templateFooter", templateFooter);

      // Process call-to-actions
      if (interactiveAction === "cta" || interactiveAction === "all") {
        callToActions
          .filter((cta) => cta.buttonTitle)
          .forEach((cta, index) => {
            formData.append(`callToActions[${index}][type]`, cta.type);
            formData.append(
              `callToActions[${index}][buttonTitle]`,
              cta.buttonTitle
            );

            // For phone numbers, combine country code and number
            const buttonValue =
              cta.type === "phone"
                ? `${cta.countryCode}${cta.buttonValue}`
                : cta.buttonValue;

            formData.append(
              `callToActions[${index}][buttonValue]`,
              buttonValue
            );
            formData.append(
              `callToActions[${index}][countryCode]`,
              cta.countryCode
            );
          });
      }

      // Process quick replies
      if (interactiveAction === "quick" || interactiveAction === "all") {
        quickReplies.forEach((reply, index) => {
          formData.append(`quickReplies[${index}]`, reply);
        });
      }

      // Process image file
      if (templateType === "image" && imageFile) {
        formData.append("file", imageFile);
      }

      // Validate required fields
      if (!templateName || !templateType) {
        toast.error("Template Name and Template Type are required.");
        return;
      }

      // Validate CTA fields
      for (const cta of callToActions) {
        if (cta.buttonTitle && cta.type === "url") {
          const urlPattern = /^(ftp|http|https):\/\/[^ "]+$/;
          if (!urlPattern.test(cta.buttonValue)) {
            toast.error("Please enter a valid URL.");
            return;
          }
        }
        if (cta.buttonTitle && cta.type === "phone") {
          const phonePattern = /^[0-9]+$/;
          if (!phonePattern.test(cta.buttonValue)) {
            toast.error("Please enter a valid phone number.");
            return;
          }
          if (!cta.countryCode.startsWith("+")) {
            toast.error("Country code must start with '+'");
            return;
          }
        }
      }

      // Submit to backend
      const response = await axios.post(
        `${BASE_URL}admin/whatsapp-template`,
        formData,
        {
          headers: {
            "x-access-token": token,
            version: "1.0.0",
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Reset form on success
      if (response.data.success) {
        toast.success("Template created successfully!");
        setTemplateName("");
        setTemplateType("");
        setTemplateFormat("");
        setTemplateFooter("");
        setInteractiveAction("none");
        setQuickReplies([]);
        setCallToActions([
          {
            type: "",
            buttonTitle: "",
            buttonValue: "",
            countryCode: "+91",
          },
        ]);
        setImageFile(null);
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error(error.response?.data?.message || "Failed to create template");
    }
  };

  // Add/remove CTA buttons
  const handleAddCallToAction = () => {
    if (callToActions.length < 2) {
      setCallToActions([
        ...callToActions,
        { type: "", buttonTitle: "", buttonValue: "", countryCode: "+91" },
      ]);
    }
  };

  const handleRemoveCallToAction = (index) => {
    if (callToActions.length > 1) {
      setCallToActions(callToActions.filter((_, i) => i !== index));
    }
  };

  // Handle file upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "video/mp4",
      "application/pdf",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PNG, JPG, MP4, and PDF files are allowed");
      return;
    }

    if (file.type.startsWith("image/")) {
      setFileType("image");
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      setFileType("video");
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setFileType("pdf");
      setPreviewUrl(null);
    }
    setImageFile(file);
  };

  return (
    <Box sx={{ 
      display: "flex", 
      gap: { xs: "10px", md: "20px" }, 
      width: "100%", 
      flexWrap: "wrap", 
      padding: { xs: "10px", sm: "15px", md: "20px" },
      boxSizing: "border-box",
      height: "100%",
      overflow: "hidden",
    }}>
      {/* Left Side - Form Controls */}
      <Card sx={{ 
        flex: { xs: "100%", md: 1 }, 
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", 
        width: { xs: "100%", md: "auto" },
        maxWidth: "100%",
        overflow: "auto",
      }}>
        <CardContent sx={{ padding: { xs: "12px", sm: "14px", md: "16px" } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, mb: { xs: 1, md: 2 } }}>
            Template Configuration
          </Typography>

          {/* Credit Warning Banner */}
          {showWarning && !isViewMode && (
            <Box sx={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "6px",
              padding: "12px 15px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}>
              <Box component="i" className="fa fa-exclamation-triangle" sx={{ color: "#856404", fontSize: "20px" }} />
              <Box>
                <Typography component="strong" sx={{ color: "#856404", fontWeight: 600, display: "block" }}>
                  Insufficient ₹
                </Typography>
                <Typography sx={{ margin: "5px 0 0 0", color: "#856404", fontSize: "14px" }}>
                  Your WhatsApp ₹ balance is 0. Please recharge your account to create WhatsApp templates.
                </Typography>
              </Box>
            </Box>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Box sx={{ display: "flex", gap: 2, mb: { xs: 2, md: 3 }, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                fullWidth
                label="Template Name"
                disabled={isViewMode}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
              <FormControl fullWidth>
                <InputLabel>Template Type</InputLabel>
                <Select
                  disabled={isViewMode}
                  value={templateType}
                  label="Template Type"
                  onChange={(e) => setTemplateType(e.target.value)}
                >
                  <MenuItem value="">Select message type</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="image">Text With Media</MenuItem>
                </Select>
              </FormControl>
              {templateType === "image" && !isViewMode && (
                <Tooltip title="Upload media">
                  <label
                    style={{
                      cursor: "pointer",
                      color: "#1a73e8",
                      textDecoration: "underline",
                    }}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="*"
                      onChange={handleImageUpload}
                    />
                  </label>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ mb: { xs: 2, md: 3 } }}>
              <TextField
                fullWidth
                label="Template Format"
                multiline
                rows={4}
                value={templateFormat}
                onChange={(e) => setTemplateFormat(e.target.value)}
                disabled={isViewMode}
                inputProps={{ maxLength: maxChars }}
                helperText={`${
                  templateFormat.length
                }/${maxChars} characters | ${
                  templateFormat.trim().split(/\s+/).filter(Boolean).length
                } words`}
              />
            </Box>

            <Box sx={{ mb: { xs: 2, md: 3 } }}>
              <TextField
                fullWidth
                label="Template Footer (Optional)"
                value={templateFooter}
                onChange={(e) => setTemplateFooter(e.target.value)}
                disabled={isViewMode}
                inputProps={{ maxLength: footerMaxChars }}
                helperText={`${templateFooter.length}/${footerMaxChars} characters`}
              />
            </Box>

            <Box sx={{ mb: { xs: 2, md: 3 } }}>
              <Typography sx={{ mb: 1 }}>Interactive Actions</Typography>
              <RadioGroup
                value={interactiveAction}
                onChange={handleInteractiveChange}
                row
                disabled={isViewMode}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio disabled={isViewMode} />}
                  label="None"
                />
                <FormControlLabel
                  value="cta"
                  control={<Radio disabled={isViewMode} />}
                  label="Call to Actions"
                />
              </RadioGroup>
            </Box>

            {(interactiveAction === "cta" || interactiveAction === "all") && (
              <Box sx={{ mb: { xs: 2, md: 3 } }}>
                {callToActions.map((cta, index) => (
                  <Box
                    key={index}
                    sx={{
                      bgcolor: "#f5f5f5",
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography>Call to Action {index + 1}:</Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCallToAction(index)}
                        disabled={isViewMode || callToActions.length <= 1}
                      >
                        <X size={16} />
                      </IconButton>
                    </Box>

                    <FormControl fullWidth sx={{ mb: 2 }} disabled={isViewMode}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={cta.type}
                        label="Type"
                        onChange={(e) =>
                          handleCallToActionChange(
                            index,
                            "type",
                            e.target.value
                          )
                        }
                      >
                        <MenuItem value="">Select type</MenuItem>
                        <MenuItem value="url">URL</MenuItem>
                        <MenuItem value="phone">Phone Number</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Button Title"
                      value={cta.buttonTitle}
                      onChange={(e) =>
                        handleCallToActionChange(
                          index,
                          "buttonTitle",
                          e.target.value.replace(/[0-9]/g, "")
                        )
                      }
                      disabled={isViewMode}
                      inputProps={{ maxLength: buttonTitleMaxChars }}
                      helperText={`${cta.buttonTitle.length}/${buttonTitleMaxChars}`}
                      sx={{ mb: 2 }}
                    />

                    {cta.type === "phone" ? (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {/* Country Code Field */}
                        <TextField
                          label="Country Code"
                          value={cta.countryCode}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (/^\+?\d*$/.test(newValue)) {
                              handleCallToActionChange(
                                index,
                                "countryCode",
                                newValue
                              );
                            }
                          }}
                          disabled={isViewMode}
                          sx={{ width: "50%" }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Phone Number"
                          value={cta.buttonValue}
                          onChange={(e) => {
                            const newValue = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            ); // Removes non-numeric characters
                            handleCallToActionChange(
                              index,
                              "buttonValue",
                              newValue
                            );
                          }}
                          disabled={isViewMode}
                        />
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        label="URL"
                        value={cta.buttonValue}
                        onChange={(e) =>
                          handleCallToActionChange(
                            index,
                            "buttonValue",
                            e.target.value
                          )
                        }
                        disabled={isViewMode}
                      />
                    )}
                  </Box>
                ))}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleAddCallToAction}
                    disabled={isViewMode || callToActions.length >= 2}
                    startIcon={<Plus size={16} />}
                  >
                    Add CTA
                  </Button>
                </Box>
              </Box>
            )}

            {!isViewMode && (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 1 }}
                disabled={whatsappBalance === 0}
                style={whatsappBalance === 0 ? { cursor: "not-allowed", opacity: 0.6 } : {}}
              >
                Submit
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Right Side - Preview */}
      <Card
        sx={{
          flex: { xs: "100%", md: 1 },
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          width: { xs: "100%", md: "auto" },
          maxWidth: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent sx={{ 
          display: "flex", 
          flexDirection: "column",
          padding: { xs: "10px", sm: "12px", md: "16px" },
          flex: 1,
          overflow: "hidden",
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 1, md: 2 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, mb: 0 }}>
              Preview
            </Typography>
            {isViewMode && imageFile && (
              <button
                onClick={() => downloadFile(imageFile)}
                style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  padding: "8px 12px",
                  borderRadius: "5px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Download File
              </button>
            )}
          </Box>

          {/* Phone preview implementation */}
          <Box sx={{ 
            width: "100%", 
            display: "flex", 
            justifyContent: "center",
            overflow: "visible",
            flex: 1,
          }}>
          <div style={phoneStyles.container}>
            <div style={phoneStyles.dynamicIsland}></div>
            <div style={phoneStyles.screen}>
              <div style={phoneStyles.statusBar}>
                <span>9:41</span>
                <span>100%</span>
              </div>
              <div style={phoneStyles.whatsappHeader}>
                <MessageSquare size={Math.max(16, 20 * scale)} />
                <div>
                  <Typography variant="subtitle2" sx={{ fontSize: `${14 * scale}px` }}>WhatsApp Business</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: `${12 * scale}px` }}>
                    Template Preview
                  </Typography>
                </div>
              </div>
              <div style={phoneStyles.chatBackground}>
                <div style={phoneStyles.messageBubble}>
                  {templateType === "image" && (
                    <>
                      {fileType === "image" && (
                        <div style={phoneStyles.imagePreview}>
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt="Uploaded"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                            />
                          ) : (
                            <ImageIcon
                              sx={{ fontSize: 48, color: "#9e9e9e" }}
                            />
                          )}
                        </div>
                      )}
                      {fileType === "video" && (
                        <div style={phoneStyles.imagePreview}>
                          <video
                            controls
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "8px",
                            }}
                          >
                            <source src={previewUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )}
                      {fileType === "pdf" && (
                        <div style={phoneStyles.imagePreview}>
                          <PictureAsPdf sx={{ fontSize: 48, color: "red" }} />
                        </div>
                      )}
                    </>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {templateFormat || "Your message will appear here"}
                  </Typography>
                  {templateFooter && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        color: "text.secondary",
                      }}
                    >
                      {templateFooter}
                    </Typography>
                  )}
                </div>

                {(interactiveAction === "cta" ||
                  interactiveAction === "all") && (
                  <Box style={phoneStyles.actionButtonWrapper}>
                    {callToActions.map(
                      (cta, index) =>
                        cta.buttonTitle && (
                          <button style={phoneStyles.actionButton} key={index}>
                            {cta.type === "phone" ? (
                              <Phone size={16} style={{ marginRight: 8 }} />
                            ) : (
                              <LinkIcon size={16} style={{ marginRight: 8 }} />
                            )}
                            {cta.buttonTitle}
                          </button>
                        )
                    )}
                  </Box>
                )}

                {(interactiveAction === "quick" ||
                  interactiveAction === "all") &&
                  quickReplies.length > 0 && (
                    <Box style={phoneStyles.actionButtonWrapper}>
                      {quickReplies.map((reply, index) => (
                        <button key={index} style={phoneStyles.quickReply}>
                          {reply}
                        </button>
                      ))}
                    </Box>
                  )}
              </div>
            </div>
          </div>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
